'use client';

import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { Fragment, type JSX, type PropsWithChildren } from 'react';
import { GTM_EVENT_NAME_EXPAND_SECTION, GTM_EVENT_NAME_FAQS_VIEWED } from '@shared/utils/constants/gtm';
import { sendGtmEvent } from '@shared/utils/helpers/send-gtm-event';
import { useControlled } from '@shared/utils/hooks/use-controlled';
import { Chevron, MinusIcon, PlusIcon } from '../svgs';
import styles from './accordion.module.scss';
const ExpandedIcon = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span className={styles["toggle-icon"]}><MinusIcon /></span>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const CollapsedIcon = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span className={styles["toggle-icon"]}><PlusIcon /></span>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
export type AccordionProps = PropsWithChildren<{
  className?: string;
  headerLabel: string;
  headerLevel?: number;
  iconPosition?: 'left' | 'right';
  isExpanded?: boolean;
  isFaqAccordion?: boolean;
  onChange?: (isExpanded: boolean) => void;
  withPlusMinusIcon?: boolean;
}>;
export const Accordion = t0 => {
  const $ = _c(38);
  const {
    children,
    className,
    headerLabel,
    headerLevel,
    iconPosition: t1,
    isExpanded: controlledIsExpanded,
    isFaqAccordion: t2,
    onChange,
    withPlusMinusIcon: t3
  } = t0;
  const iconPosition = t1 === undefined ? "left" : t1;
  const isFaqAccordion = t2 === undefined ? false : t2;
  const withPlusMinusIcon = t3 === undefined ? false : t3;
  let t4;
  if ($[0] !== controlledIsExpanded) {
    t4 = {
      controlledValue: controlledIsExpanded,
      defaultValue: false
    };
    $[0] = controlledIsExpanded;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const [isExpanded, setIsExpanded] = useControlled(t4);
  let t5;
  if ($[2] !== headerLabel || $[3] !== isExpanded || $[4] !== isFaqAccordion || $[5] !== onChange || $[6] !== setIsExpanded) {
    t5 = () => {
      if (!isExpanded) {
        sendGtmEvent({
          eventName: isFaqAccordion ? GTM_EVENT_NAME_FAQS_VIEWED : GTM_EVENT_NAME_EXPAND_SECTION,
          label: headerLabel
        });
      }
      setIsExpanded(!isExpanded);
      if (onChange) {
        onChange(!isExpanded);
      }
    };
    $[2] = headerLabel;
    $[3] = isExpanded;
    $[4] = isFaqAccordion;
    $[5] = onChange;
    $[6] = setIsExpanded;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const handleAccordionTriggerButtonClick = t5;
  let t6;
  if ($[8] !== isExpanded || $[9] !== withPlusMinusIcon) {
    t6 = () => {
      if (withPlusMinusIcon) {
        return isExpanded ? <ExpandedIcon /> : <CollapsedIcon />;
      }
      return <Chevron className={classNames(styles["accordion__trigger-icon"], {
        [styles["accordion__trigger-icon--is-open"]]: isExpanded
      })} />;
    };
    $[8] = isExpanded;
    $[9] = withPlusMinusIcon;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const renderTriggerIcon = t6;
  const HeaderWrapper = headerLevel ? `h${headerLevel}` as keyof JSX.IntrinsicElements : Fragment;
  let t7;
  if ($[11] !== className || $[12] !== withPlusMinusIcon) {
    t7 = classNames(styles.accordion, className, {
      [styles["accordion__plus-minus"]]: withPlusMinusIcon
    });
    $[11] = className;
    $[12] = withPlusMinusIcon;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = iconPosition === "right";
  let t9;
  if ($[14] !== t8) {
    t9 = classNames(styles.accordion__trigger, {
      [styles["accordion__trigger--spaced-out"]]: t8
    });
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== iconPosition || $[17] !== renderTriggerIcon) {
    t10 = iconPosition === "left" && renderTriggerIcon();
    $[16] = iconPosition;
    $[17] = renderTriggerIcon;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== headerLabel) {
    t11 = <span className={styles.accordion__label}>{headerLabel}</span>;
    $[19] = headerLabel;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== iconPosition || $[22] !== renderTriggerIcon) {
    t12 = iconPosition === "right" && renderTriggerIcon();
    $[21] = iconPosition;
    $[22] = renderTriggerIcon;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== handleAccordionTriggerButtonClick || $[25] !== isExpanded || $[26] !== t10 || $[27] !== t11 || $[28] !== t12 || $[29] !== t9) {
    t13 = <button aria-expanded={isExpanded} className={t9} data-testid="accordion-button" id="accordion-button" onClick={handleAccordionTriggerButtonClick} type="button">{t10}{t11}{t12}</button>;
    $[24] = handleAccordionTriggerButtonClick;
    $[25] = isExpanded;
    $[26] = t10;
    $[27] = t11;
    $[28] = t12;
    $[29] = t9;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== HeaderWrapper || $[32] !== t13) {
    t14 = <HeaderWrapper>{t13}</HeaderWrapper>;
    $[31] = HeaderWrapper;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  const t15 = isExpanded && children;
  let t16;
  if ($[34] !== t14 || $[35] !== t15 || $[36] !== t7) {
    t16 = <div className={t7}>{t14}{t15}</div>;
    $[34] = t14;
    $[35] = t15;
    $[36] = t7;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  return t16;
};